// 關鍵字的搜尋介面
import React, { useState } from 'react';
import { LoadingOverlay, Modal, TagsInput, Text } from '@mantine/core';
import KeywordsSearchResultCard from '../../ui/places/KeywordsSearchResultCard.tsx';
import { PlaceWithServices } from '../../../models/Place.ts';
import { SearchPlacesByKeywordRequest } from '../../../api/requests/place-request.ts';
import useSearchPlacesByKeyword from '../../../hooks/places/useSearchPlacesByKeyword.ts';
import { useMap } from '@vis.gl/react-google-maps';

interface KeywordsSearchModalProps {
  setIsSearchModalOpen: (isSearchModalOpen: boolean) => void;
  setSelectedLocation: (selectedLocation: PlaceWithServices) => void;
  isNearbyPlacesListOpen: boolean;
  defaultCenterSelectedZoom: number;
  setHasUserMovedMap: (hasUserMoved: boolean) => void;
}

const KeywordsSearchModal: React.FC<KeywordsSearchModalProps> = ({
  setIsSearchModalOpen,
  setSelectedLocation,
  isNearbyPlacesListOpen,
  defaultCenterSelectedZoom,
  setHasUserMovedMap,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const map = useMap();

  const searchPlacesByKeywordRequest: SearchPlacesByKeywordRequest = {
    keywords: tags,
  };

  const { data: searchResult, isLoading: isSearchResultLoading } =
    useSearchPlacesByKeyword(searchPlacesByKeywordRequest);

  const handleKeywordsSearchResultCardClick = (place: PlaceWithServices) => {
    setSelectedLocation(place);
    setIsSearchModalOpen(false);
    setTags([]);

    // 設定地圖中心點
    map.setCenter({
      lat: place.location.lat,
      lng: place.location.lng,
    });
    map.setZoom(defaultCenterSelectedZoom);
    // 資訊卡牌再選定地點時預設都會展開, 故 要把地圖中心稍微移動, 以避免被遮蓋住
    map.panBy(-170, 0);
    // 如果附近場站卡牌也有展開, 就需要另外再 panBy -200
    if (isNearbyPlacesListOpen) map.panBy(-200, 0);

    setHasUserMovedMap(false);
  };

  return (
    <Modal
      opened
      onClose={() => {
        setIsSearchModalOpen(false);
        setTags([]);
      }}
      centered
      size={'532px'}
      padding="md"
      radius="md"
      withCloseButton={false}
      styles={{ body: { width: 532 } }}
    >
      {/* TagsInput 當作搜尋框 */}
      <TagsInput
        value={tags}
        onChange={setTags}
        placeholder="請輸入多組關鍵字（按下 Enter 即可新增）"
        splitChars={[]}
        clearable
      />

      {/* 搜尋結果區域 */}
      <div
        style={{
          marginTop: 20,
          minHeight: 120,
          maxHeight: 500,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {isSearchResultLoading && (
          <LoadingOverlay
            visible
            zIndex={5}
            overlayProps={{
              blur: 1,
              radius: 'sm',
              backgroundOpacity: 0.3,
            }}
            loaderProps={{ type: 'bars' }}
          />
        )}

        {/* 搜尋結果內容 */}
        {!tags.length ? (
          // 尚未搜尋的時候
          <div
            style={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'grey',
            }}
          >
            <Text pt={50} pb={50}>
              🔎 請輸入搜尋關鍵字
            </Text>
          </div>
        ) : searchResult?.length ? (
          // 取得搜尋結果時
          searchResult.map((place) => (
            <div key={place.id} style={{ marginBottom: 12 }}>
              <KeywordsSearchResultCard
                place={place}
                onClick={() => handleKeywordsSearchResultCardClick(place)}
              />
            </div>
          ))
        ) : (
          // 沒有搜尋結果時
          <div
            style={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'grey',
            }}
          >
            <Text pt={50} pb={50}>
              無相關地點
            </Text>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default KeywordsSearchModal;
